import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import {
  GraduationCap,
  Heart,
  Users,
  Clock,
  Phone,
  Mail,
  MapPin,
  Star,
  BookOpen,
  Shield,
  Sparkles,
  ChevronDown,
  Menu,
  X,
  CheckCircle,
  Sun,
  Apple,
  Brain,
  Quote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/expicvsf_ZAM%20Learning%20Center%20Main%20Logo.png";

// Contact Info
const CONTACT_INFO = {
  phone: "301-220-2514",
  email: "zamlearningcenter@gmail.com",
  address: "5023 Geronimo St, College Park, MD 20740",
  googleMapsEmbed: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3105.8!2d-76.9369!3d38.9897!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7c6de5af6e45b%3A0x0!2s5023%20Geronimo%20St%2C%20College%20Park%2C%20MD%2020740!5e0!3m2!1sen!2sus!4v1234567890",
};

// Real ZAM Learning Center Photos
const IMAGES = {
  // Kids building with magnetic tiles
  magneticTiles: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/1d54cjdm_DSC02573.jpeg",
  // Kids doing group activity/science
  groupActivity: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/zopagpyh_DSCN1601.jpeg",
  // Kids in costumes doing arts and crafts
  crafts: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/ac59zkju_DSC_7336.jpeg",
  // Kids learning together
  learning: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/z1vwncb4_DSC02067.jpeg",
  
  // Mapped for sections
  hero: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/1d54cjdm_DSC02573.jpeg",
  about: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/zopagpyh_DSCN1601.jpeg",
  program2_3: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/ac59zkju_DSC_7336.jpeg",
  program4_5: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/z1vwncb4_DSC02067.jpeg",
  hours: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/1d54cjdm_DSC02573.jpeg",
  hero2: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/zopagpyh_DSCN1601.jpeg",
  hero3: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/ac59zkju_DSC_7336.jpeg",
  hero4: "https://customer-assets.emergentagent.com/job_professional-zam/artifacts/z1vwncb4_DSC02067.jpeg",
};

// Testimonials Data
const testimonials = [
  {
    name: "Pavel",
    location: "Daniels Park",
    text: "We are so fortunate to have this gem of an in-home daycare and preschool in our neighborhood! Our daughter started at ZAM around 2.5 yo and stayed there right until Kindergarten. We actually turned down a spot at the UMD CYC that we were on the waitlist for for 3 years to stay at ZAM. There is so much we loved about ZAM, most importantly - that teachers genuinely care about the kids and their wellbeing.",
    highlight: "Turned down UMD CYC to stay at ZAM",
  },
  {
    name: "Laura",
    location: "Hollywood",
    text: "Our daughter spent 3 years at ZAM and we couldn't be more happy with the experience. She blossomed from an almost-2-year-old with catching up to do, to an advanced almost-5-year-old. Mrs. Jessa is a kind, keen, unflappable, and supportive teacher and caretaker. She provides an excellent balance between learning through academics and through play.",
    highlight: "Child blossomed over 3 years",
  },
  {
    name: "Terah",
    location: "Parent",
    text: "We absolutely loved our time at ZAM. Our daughter spent 3.5 years there, and during that time we watched her truly flourish. Ms. Jessa has an incredible gift for seeing each child as an individual and caring for them both academically and holistically. Our first-time parent anxieties quickly melted away.",
    highlight: "First-time parent anxieties melted away",
  },
  {
    name: "Tamara",
    location: "Parent",
    text: "My son left ZAM soooooo ready for kindergarten, he has consistently tested in the 90th percentile, or higher, on all his standardized assessments. He's only been in kindergarten for 6 months. Ms. Sam and Jessa are kind-hearted and committed to each child that enters their door.",
    highlight: "Child tests in 90th percentile",
  },
  {
    name: "Bahar",
    location: "Parent",
    text: "Ms. Jessa is a compassionate, caring, and dedicated teacher whose purpose is to create a safe learning environment to equip our children with the skills that they need to be ready for the next academic stage of their lives. I can 100% recommend to anyone considering ZAM Learning Center.",
    highlight: "100% recommended",
  },
  {
    name: "Sasha",
    location: "Parent",
    text: "We felt so fortunate to have this gem of an in-home daycare and preschool in our neighborhood! Ms. Jessa cooks fresh homemade meals for the kids, with lots of fresh fruit and vegetables. They use Tucker Turtle technique to teach kids to recognize their emotions. 10/10 would recommend this daycare!",
    highlight: "10/10 would recommend",
  },
];

// Features Data
const features = [
  {
    icon: Heart,
    title: "Safe & Nurturing Environment",
    description: "A warm, family-like atmosphere where every child feels valued, loved, and encouraged to explore.",
  },
  {
    icon: GraduationCap,
    title: "Kindergarten Ready",
    description: "Strong focus on early literacy, math, and social-emotional skills to ensure your child excels in kindergarten.",
  },
  {
    icon: Users,
    title: "Mixed-Age Learning",
    description: "Older children mentor younger ones, creating a sibling-like culture of care and collaborative learning.",
  },
  {
    icon: Apple,
    title: "Fresh Homemade Meals",
    description: "Nutritious, home-cooked meals prepared daily with fresh fruits and vegetables included.",
  },
  {
    icon: Sun,
    title: "Outdoor Play Time",
    description: "2+ hours of outdoor play daily when weather permits, with consistent sunscreen application.",
  },
  {
    icon: Brain,
    title: "Emotional Intelligence",
    description: "Tucker Turtle technique teaches children to recognize emotions and solve conflicts independently.",
  },
];

// Programs Data
const programs = [
  {
    age: "2-3",
    title: "Toddler Program",
    description: "Our 2–3 year old program provides a nurturing space where young learners grow through hands-on exploration and social interaction.",
    highlights: [
      "Hands-on exploration activities",
      "Social skill development",
      "Learning from older peers",
      "Building confidence & independence",
      "Potty training support",
    ],
    image: IMAGES.crafts,
  },
  {
    age: "4-5",
    title: "Pre-K Program",
    description: "Our 4–5 year old program encourages school readiness while fostering responsibility and leadership skills.",
    highlights: [
      "Kindergarten preparation",
      "Leadership opportunities",
      "Critical thinking development",
      "Early literacy & math",
      "Science experiments & activities",
    ],
    image: IMAGES.magneticTiles,
  },
];

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm shadow-sm" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => scrollToSection("hero")}>
            <img 
              src={LOGO_URL} 
              alt="ZAM Learning Center Logo" 
              className="h-12 md:h-16 w-auto object-contain"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection("about")} className="text-gray-600 hover:text-purple-600 transition-colors font-medium">
              About
            </button>
            <button onClick={() => scrollToSection("programs")} className="text-gray-600 hover:text-purple-600 transition-colors font-medium">
              Programs
            </button>
            <button onClick={() => scrollToSection("features")} className="text-gray-600 hover:text-purple-600 transition-colors font-medium">
              Why ZAM
            </button>
            <button onClick={() => scrollToSection("testimonials")} className="text-gray-600 hover:text-purple-600 transition-colors font-medium">
              Reviews
            </button>
            <Button onClick={() => scrollToSection("contact")} className="bg-purple-600 hover:bg-purple-700" data-testid="nav-contact-btn">
              Contact Us
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-btn">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden pb-4 border-t">
            <div className="flex flex-col gap-2 pt-4">
              <button onClick={() => scrollToSection("about")} className="text-left px-4 py-2 text-gray-600 hover:bg-purple-50 rounded-lg">
                About
              </button>
              <button onClick={() => scrollToSection("programs")} className="text-left px-4 py-2 text-gray-600 hover:bg-purple-50 rounded-lg">
                Programs
              </button>
              <button onClick={() => scrollToSection("features")} className="text-left px-4 py-2 text-gray-600 hover:bg-purple-50 rounded-lg">
                Why ZAM
              </button>
              <button onClick={() => scrollToSection("testimonials")} className="text-left px-4 py-2 text-gray-600 hover:bg-purple-50 rounded-lg">
                Reviews
              </button>
              <Button onClick={() => scrollToSection("contact")} className="mx-4 bg-purple-600 hover:bg-purple-700">
                Contact Us
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section id="hero" className="pt-20 md:pt-24 min-h-screen flex items-center bg-gradient-to-br from-purple-50 via-white to-pink-50" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-center lg:text-left">
            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 mb-4">
              <Star className="w-3 h-3 mr-1 fill-purple-500" /> Maryland Excels Certified
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Empowering Little Minds to
              <span className="text-purple-600 block">Shine & Explore</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-xl mx-auto lg:mx-0">
              A warm, nurturing family daycare where your child learns, grows, and thrives. 
              Preparing children ages 2-5 for kindergarten success through play-based learning and personalized care.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-lg px-8" onClick={() => scrollToSection("contact")} data-testid="hero-schedule-tour-btn">
                Schedule a Tour
              </Button>
              <Button size="lg" variant="outline" className="border-purple-600 text-purple-600 hover:bg-purple-50 text-lg px-8" onClick={() => scrollToSection("programs")} data-testid="hero-view-programs-btn">
                View Programs
              </Button>
            </div>
            
            {/* Trust Indicators */}
            <div className="mt-10 flex flex-wrap justify-center lg:justify-start gap-6">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-600" />
                <span className="text-sm text-gray-600">7AM - 6PM</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                <span className="text-sm text-gray-600">Ages 2-5</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                <span className="text-sm text-gray-600">Licensed & Insured</span>
              </div>
            </div>
          </div>

          {/* Right Content - Image Grid */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <img
                  src={IMAGES.magneticTiles}
                  alt="Children building with magnetic tiles at ZAM"
                  className="rounded-2xl shadow-lg w-full h-48 md:h-64 object-cover"
                />
                <img
                  src={IMAGES.groupActivity}
                  alt="Kids doing group activities at ZAM"
                  className="rounded-2xl shadow-lg w-full h-32 md:h-40 object-cover"
                />
              </div>
              <div className="space-y-4 pt-8">
                <img
                  src={IMAGES.crafts}
                  alt="Children in costumes doing arts and crafts at ZAM"
                  className="rounded-2xl shadow-lg w-full h-32 md:h-40 object-cover"
                />
                <img
                  src={IMAGES.learning}
                  alt="Kids learning together at ZAM"
                  className="rounded-2xl shadow-lg w-full h-48 md:h-64 object-cover"
                />
              </div>
            </div>
            {/* Floating Badge */}
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 bg-white rounded-full px-6 py-3 shadow-lg border-2 border-purple-100">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-sm font-medium text-gray-700">5-Star Reviews</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-16 md:py-24 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <div className="relative">
            <img
              src={IMAGES.groupActivity}
              alt="Children doing group activities at ZAM Learning Center"
              className="rounded-2xl shadow-xl w-full h-[400px] md:h-[500px] object-cover"
            />
            <div className="absolute -bottom-6 -right-6 bg-purple-600 text-white rounded-2xl p-6 shadow-lg hidden md:block">
              <p className="text-3xl font-bold">10+</p>
              <p className="text-sm">Years of Excellence</p>
            </div>
          </div>

          {/* Content */}
          <div>
            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 mb-4">
              About Us
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Welcome to ZAM Learning Center
            </h2>
            <p className="text-gray-600 mb-6 text-lg">
              At ZAM Learning Center, we believe in fostering a love for learning through a well-rounded program that supports language, cognitive, social-emotional, and physical development.
            </p>
            <p className="text-gray-600 mb-8">
              Our dedicated team of caregivers, led by Ms. Jessa and Ms. Sam, provides personalized attention, ensuring every child feels valued and encouraged. We're not just a daycare – we're an extended family committed to your child's success.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="text-center p-4 bg-purple-50 rounded-xl">
                <p className="text-2xl md:text-3xl font-bold text-purple-600">2-5</p>
                <p className="text-sm text-gray-600">Age Range</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-xl">
                <p className="text-2xl md:text-3xl font-bold text-purple-600">11</p>
                <p className="text-sm text-gray-600">Hours Daily</p>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-xl">
                <p className="text-2xl md:text-3xl font-bold text-purple-600">5★</p>
                <p className="text-sm text-gray-600">Rating</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <Badge variant="outline" className="py-2 px-4 border-purple-200">
                <CheckCircle className="w-4 h-4 mr-2 text-purple-600" /> Maryland Excels
              </Badge>
              <Badge variant="outline" className="py-2 px-4 border-purple-200">
                <CheckCircle className="w-4 h-4 mr-2 text-purple-600" /> Licensed Provider
              </Badge>
              <Badge variant="outline" className="py-2 px-4 border-purple-200">
                <CheckCircle className="w-4 h-4 mr-2 text-purple-600" /> CPR Certified
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Programs Section
const ProgramsSection = () => {
  return (
    <section id="programs" className="py-16 md:py-24 bg-gradient-to-br from-purple-50 to-pink-50" data-testid="programs-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 mb-4">
            Our Programs
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Age-Appropriate Learning Programs
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Our mixed-age programs encourage peer learning, where older children mentor younger ones, creating a supportive community.
          </p>
        </div>

        {/* Programs Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {programs.map((program, index) => (
            <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow border-0 shadow-lg" data-testid={`program-card-${index}`}>
              <div className="relative h-48 md:h-56">
                <img
                  src={program.image}
                  alt={program.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-purple-600 text-white px-4 py-2 rounded-full font-bold">
                  Ages {program.age}
                </div>
              </div>
              <CardHeader>
                <CardTitle className="text-2xl text-gray-900">{program.title}</CardTitle>
                <CardDescription className="text-base">{program.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {program.highlights.map((highlight, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-600">
                      <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0" />
                      {highlight}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  return (
    <section id="features" className="py-16 md:py-24 bg-white" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 mb-4">
            Why Choose Us
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            What Makes ZAM Special
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Parents choose ZAM for our unique combination of academic excellence, nurturing care, and family-like environment.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-all hover:-translate-y-1 bg-gradient-to-br from-white to-purple-50/30" data-testid={`feature-card-${index}`}>
              <CardHeader>
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-7 h-7 text-purple-600" />
                </div>
                <CardTitle className="text-xl text-gray-900">{feature.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section - FIXED: Light background with readable text
const TestimonialsSection = () => {
  return (
    <section id="testimonials" className="py-16 md:py-24 bg-gradient-to-br from-purple-50 to-pink-50" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 mb-4">
            Parent Reviews
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            What Parents Say About Us
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Don't just take our word for it – hear from the families who have experienced the ZAM difference.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="bg-white shadow-lg hover:shadow-xl transition-shadow border border-purple-100" data-testid={`testimonial-card-${index}`}>
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <CardTitle className="text-gray-900 text-lg">{testimonial.name}</CardTitle>
                    <p className="text-purple-600 text-sm">{testimonial.location}</p>
                  </div>
                </div>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
              </CardHeader>
              <CardContent>
                <Quote className="w-8 h-8 text-purple-200 mb-2" />
                <p className="text-gray-700 text-sm leading-relaxed mb-4">{testimonial.text}</p>
                <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100">
                  {testimonial.highlight}
                </Badge>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Hours Section
const HoursSection = () => {
  return (
    <section className="py-16 md:py-24 bg-white" data-testid="hours-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div>
            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 mb-4">
              Hours & Schedule
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Convenient Hours for Working Parents
            </h2>
            <p className="text-gray-600 mb-8 text-lg">
              We understand the demands of working parents. Our extended hours ensure you have the flexibility you need without compromising on quality care.
            </p>

            <div className="space-y-6">
              {/* Operating Hours */}
              <Card className="border-purple-200 bg-purple-50/50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Operating Hours</h3>
                      <p className="text-purple-600 font-medium">Monday - Friday: 7:00 AM to 6:00 PM</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Open Year Round */}
              <Card className="border-purple-200 bg-purple-50/50">
                <CardContent className="pt-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                      <Sun className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Open Year Round</h3>
                      <p className="text-gray-600">Consistent care you can count on</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Closures */}
              <Card className="border-gray-200">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-3">Scheduled Closures</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-gray-400" />
                      All Federal Holidays
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-gray-400" />
                      2 Weeks Provider Vacation (announced in advance)
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Image */}
          <div className="relative">
            <img
              src={IMAGES.learning}
              alt="Children learning together at ZAM"
              className="rounded-2xl shadow-xl w-full h-[400px] md:h-[500px] object-cover"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const faqs = [
    {
      question: "What ages do you accept?",
      answer: "We accept children ages 2 to 5 years old. Our mixed-age program allows older children to mentor younger ones, creating a supportive learning community.",
    },
    {
      question: "What are your hours of operation?",
      answer: "We are open Monday through Friday from 7:00 AM to 6:00 PM, year-round. We close on all Federal Holidays and for 2 weeks of provider vacation, which is announced well in advance.",
    },
    {
      question: "Do you provide meals?",
      answer: "Yes! Ms. Jessa prepares fresh, homemade meals daily with lots of fruits and vegetables. Meals and snacks are included in our program.",
    },
    {
      question: "How do you prepare children for kindergarten?",
      answer: "We focus on early literacy, math skills, social-emotional development, and independence. Many of our graduates test in the 90th percentile on kindergarten assessments.",
    },
    {
      question: "Do you help with potty training?",
      answer: "Absolutely! Our teachers work closely with parents to support potty training and make the transition smooth and stress-free for both children and families.",
    },
  ];

  return (
    <section className="py-16 md:py-24 bg-gray-50" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 mb-4">
            FAQ
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-xl shadow-sm border-0 px-6" data-testid={`faq-item-${index}`}>
              <AccordionTrigger className="text-left font-medium text-gray-900 hover:no-underline">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Formspree Form ID
const FORMSPREE_FORM_ID = "xkoonkqr";

// Contact Section with Google Maps and Formspree
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    childAge: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");
    
    try {
      // Submit to Formspree
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_FORM_ID}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          childAge: formData.childAge,
          message: formData.message,
          _subject: `Tour Request from ${formData.name}`,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: "", email: "", phone: "", childAge: "", message: "" });
      } else {
        const data = await response.json();
        if (data.errors) {
          setError(data.errors.map(err => err.message).join(", "));
        } else {
          setError("Something went wrong. Please try again.");
        }
      }
    } catch (err) {
      setError("Failed to send message. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 mb-4">
              Get In Touch
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Schedule a Tour Today
            </h2>
            <p className="text-gray-600 mb-8 text-lg">
              We'd love to show you around and answer any questions you have. Schedule a tour to see firsthand how we inspire young minds!
            </p>

            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Location</h3>
                  <p className="text-gray-600">{CONTACT_INFO.address}</p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Phone</h3>
                  <a href={`tel:${CONTACT_INFO.phone}`} className="text-purple-600 hover:text-purple-700 font-medium">
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Email</h3>
                  <a href={`mailto:${CONTACT_INFO.email}`} className="text-purple-600 hover:text-purple-700 font-medium">
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Hours</h3>
                  <p className="text-gray-600">Monday - Friday: 7:00 AM - 6:00 PM</p>
                </div>
              </div>
            </div>

            {/* Google Maps Embed */}
            <div className="mt-8 rounded-2xl overflow-hidden shadow-lg">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3105.5!2d-76.9369!3d38.9897!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7c6de5af6e45b%3A0x1234567890abcdef!2s5023+Geronimo+St%2C+College+Park%2C+MD+20740!5e0!3m2!1sen!2sus!4v1234567890123"
                width="100%"
                height="250"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="ZAM Learning Center Location"
                className="w-full"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <Card className="shadow-xl border-0">
              <CardHeader>
                <CardTitle className="text-2xl">Send Us a Message</CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {submitted ? (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h3>
                    <p className="text-gray-600">Thank you for your message! We'll get back to you within 24 hours.</p>
                    <Button 
                      onClick={() => setSubmitted(false)} 
                      variant="outline" 
                      className="mt-4"
                    >
                      Send Another Message
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                      <Input
                        type="text"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="w-full"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="w-full"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                        <Input
                          type="tel"
                          placeholder="(301) 123-4567"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Child's Age</label>
                        <Input
                          type="text"
                          placeholder="e.g., 3 years"
                          value={formData.childAge}
                          onChange={(e) => setFormData({ ...formData, childAge: e.target.value })}
                          className="w-full"
                          data-testid="contact-age-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
                      <Textarea
                        placeholder="I'm interested in scheduling a tour for my child..."
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        required
                        rows={4}
                        className="w-full"
                        data-testid="contact-message-input"
                      />
                    </div>
                    
                    {error && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                        {error}
                      </div>
                    )}
                    
                    <Button 
                      type="submit" 
                      className="w-full bg-purple-600 hover:bg-purple-700 text-lg py-6" 
                      data-testid="contact-submit-btn"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </Button>
                    <p className="text-xs text-gray-500 text-center mt-2">
                      Your message will be sent directly to our team.
                    </p>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Trust Badge */}
            <div className="mt-6 p-6 bg-purple-50 rounded-2xl">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-8 h-8 text-purple-600" />
                <h3 className="font-semibold text-gray-900">Your Peace of Mind</h3>
              </div>
              <p className="text-gray-600 text-sm">
                ZAM Learning Center is fully licensed, insured, and Maryland Excels certified. 
                Our teachers are CPR/First Aid certified and undergo continuous professional development.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Logo & Description */}
          <div className="md:col-span-2">
            <img 
              src={LOGO_URL} 
              alt="ZAM Learning Center Logo" 
              className="h-16 w-auto mb-4 brightness-0 invert"
            />
            <p className="text-gray-400 text-sm">
              Empowering little minds to shine and explore. Quality family child care in College Park, Maryland.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#about" className="hover:text-purple-400 transition-colors">About Us</a></li>
              <li><a href="#programs" className="hover:text-purple-400 transition-colors">Programs</a></li>
              <li><a href="#testimonials" className="hover:text-purple-400 transition-colors">Parent Reviews</a></li>
              <li><a href="#contact" className="hover:text-purple-400 transition-colors">Contact</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <a href={`tel:${CONTACT_INFO.phone}`} className="hover:text-purple-400">{CONTACT_INFO.phone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-purple-400">{CONTACT_INFO.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                <span>{CONTACT_INFO.address}</span>
              </li>
            </ul>
            <p className="text-gray-400 text-sm mt-4">
              <span className="text-purple-400 font-medium">Hours:</span> Mon-Fri, 7AM - 6PM
            </p>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8 text-center text-gray-400 text-sm">
          <p>© {new Date().getFullYear()} ZAM Learning Center. All rights reserved.</p>
          <p className="mt-2">Quality Family Child Care · College Park, Maryland</p>
        </div>
      </div>
    </footer>
  );
};

// Main Home Page
const Home = () => {
  return (
    <div className="min-h-screen">
      <Navigation />
      <HeroSection />
      <AboutSection />
      <ProgramsSection />
      <FeaturesSection />
      <TestimonialsSection />
      <HoursSection />
      <FAQSection />
      <ContactSection />
      <Footer />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="*" element={<Home />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
